import i18n from "i18next";

export const SUPPORTED_LANGUAGES = ["de", "en", "fr", "it"] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];
export const DEFAULT_LANGUAGE: SupportedLanguage = "de";
export const LANGUAGE_LABELS: Record<SupportedLanguage, string> = {
    de: "Deutsch",
    en: "English",
    fr: "Français",
    it: "Italiano"
};

export const toSupportedLanguage = (lang?: string | null): SupportedLanguage => {
    const base = (lang ?? "").split("-")[0].toLowerCase();
    return (SUPPORTED_LANGUAGES as readonly string[]).includes(base) ? (base as SupportedLanguage) : DEFAULT_LANGUAGE;
};

export const getBrowserLanguage = (): SupportedLanguage => toSupportedLanguage(window.navigator.language);

export const getLang = (): SupportedLanguage => toSupportedLanguage(i18n.language);

// Adds the active language to the variables of a query, so the call site does not have to list it.
export const withLanguage = <T extends { variables?: object }>(options: T): T => ({
    ...options,
    variables: { ...options.variables, language: getLang() }
});

// Picks the row for the active language from a list of language rows (e.g. news_mls, agendaitems_mls).
// Fallback: active language → de → first row. User-written texts often exist in one language only.
export const pickByLanguage = <T extends { language?: string | null }>(
    rows: readonly T[] | null | undefined,
    language: string = getLang()
): T | undefined => {
    if (!rows || rows.length === 0) {
        return undefined;
    }
    return rows.find((r) => r.language === language) ?? rows.find((r) => r.language === DEFAULT_LANGUAGE) ?? rows[0];
};
